import { disabledReason } from './format';
import type { Device, DeviceStatus, SubscriptionStatus } from './types';

/** Figma filter-drawer facets (layout), AC matching semantics (behavior). */
export type DeviceTypeFacet = 'Electric' | 'Fuel' | 'Hawk' | 'Sensor';

export type DeviceFilters = {
  types: DeviceTypeFacet[];
  statuses: DeviceStatus[];
  subscriptions: SubscriptionStatus[];
};

export const EMPTY_FILTERS: DeviceFilters = { types: [], statuses: [], subscriptions: [] };

export function hasActiveFilters(f: DeviceFilters): boolean {
  return f.types.length > 0 || f.statuses.length > 0 || f.subscriptions.length > 0;
}

export function activeFilterCount(f: DeviceFilters): number {
  return f.types.length + f.statuses.length + f.subscriptions.length;
}

function matchesTypeFacet(device: Device, facet: DeviceTypeFacet): boolean {
  switch (facet) {
    case 'Electric':
      return device.fuelType === 'Electric';
    case 'Fuel':
      return device.fuelType === 'Diesel';
    case 'Hawk':
      return device.category === 'Hawk';
    case 'Sensor':
      return device.kind === 'sensor';
  }
}

/**
 * Apply search + drawer filters. OR within each facet group, AND across groups
 * (story 101847). Search is case-insensitive over name + serial.
 */
export function applyDeviceFilters(
  devices: Device[],
  search: string,
  filters: DeviceFilters,
): Device[] {
  const q = search.trim().toLowerCase();
  return devices.filter((d) => {
    if (q && !`${d.name} ${d.serial}`.toLowerCase().includes(q)) return false;
    if (filters.types.length && !filters.types.some((t) => matchesTypeFacet(d, t))) return false;
    if (filters.statuses.length && !filters.statuses.includes(d.status)) return false;
    if (filters.subscriptions.length && !filters.subscriptions.includes(d.subscription.status))
      return false;
    return true;
  });
}

/** Default sort: Active → Idle → Disconnected → Not Configured → No Subscription →
 *  Pending Activation; within a group, last-sync desc (null last). (story 101847) */
const STATUS_RANK: Record<DeviceStatus, number> = { active: 0, idle: 1, disconnected: 2 };

function rank(device: Device): number {
  const disabled = disabledReason(device);
  if (disabled === 'pending_activation') return 5;
  if (disabled === 'no_subscription') return 4;
  if (disabled === 'not_configured') return 3;
  return STATUS_RANK[device.status];
}

export function sortDevices(devices: Device[]): Device[] {
  return [...devices].sort((a, b) => {
    const r = rank(a) - rank(b);
    if (r !== 0) return r;
    const ta = a.lastSyncedAt ? new Date(a.lastSyncedAt).getTime() : -Infinity;
    const tb = b.lastSyncedAt ? new Date(b.lastSyncedAt).getTime() : -Infinity;
    return tb - ta;
  });
}
